import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Gauge,
  Plus,
  ShieldCheck,
  Truck,
  UserRoundCheck,
  Wrench
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { api } from "../api";
import { useAuth } from "../auth";
import type { CompanyUnit } from "../types";

type CountItem = { name: string; value: number };
type TrendItem = { date: string; value: number };
type RecentInspection = {
  id: string;
  identifier: string;
  plate: string | null;
  unit_name?: string | null;
  result: "apto" | "apto_ressalva" | "nao_apto";
  inspector: string;
  created_at: string;
};

type Dashboard = {
  period_days: number;
  vehicles: number;
  inspections_period: number;
  inspections_month: number;
  open_ncs: number;
  maintenance_open: number;
  compliance_rate: number;
  vehicles_by_status: CountItem[];
  inspection_results: CountItem[];
  ncs_by_severity: CountItem[];
  trend: TrendItem[];
  recent_inspections: RecentInspection[];
  inspections_by_inspector: CountItem[];
  inspections_by_vehicle_type: CountItem[];
  vehicles_with_most_ncs: CountItem[];
  top_defects: CountItem[];
};

const statusLabels: Record<string, string> = {
  liberado: "Liberados",
  liberado_ressalva: "Com ressalva",
  em_manutencao: "Em manutenção",
  inativo: "Inativos"
};

const resultLabels: Record<string, string> = {
  apto: "Apto",
  apto_ressalva: "Apto c/ ressalva",
  nao_apto: "Não apto"
};

const severityLabels: Record<string, string> = {
  leve: "Leve",
  moderada: "Moderada",
  grave: "Grave",
  gravissima: "Gravíssima"
};

const statusColors: Record<string, string> = {
  liberado: "#17834b",
  liberado_ressalva: "#e2a62f",
  em_manutencao: "#cc5252",
  inativo: "#98a39d"
};

const resultColors: Record<string, string> = {
  apto: "#17834b",
  apto_ressalva: "#e2a62f",
  nao_apto: "#cc5252"
};

const severityColors: Record<string, string> = {
  leve: "#55a875",
  moderada: "#e2a62f",
  grave: "#cc5252",
  gravissima: "#8f1d2c"
};

function formatDate(value: string) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function formatDateTime(value: string) {
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function ChartEmpty({ text = "Ainda não há dados suficientes." }: { text?: string }) {
  return <div className="chart-empty">{text}</div>;
}

function DonutLegend({
  items,
  labels,
  colors
}: {
  items: CountItem[];
  labels: Record<string, string>;
  colors: Record<string, string>;
}) {
  return (
    <div className="chart-legend">
      {items.map((item) => (
        <div className="legend-row" key={item.name}>
          <span className="legend-dot" style={{ background: colors[item.name] || "#829087" }} />
          <span>{labels[item.name] || item.name}</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

function HorizontalBars({
  title,
  kicker,
  data,
  color = "#137a43",
  empty = "Sem dados para o período."
}: {
  title: string;
  kicker: string;
  data: CountItem[];
  color?: string;
  empty?: string;
}) {
  return (
    <article className="dashboard-card">
      <div className="card-heading">
        <div>
          <p className="section-kicker">{kicker}</p>
          <h2>{title}</h2>
        </div>
      </div>
      {!data.length ? (
        <ChartEmpty text={empty} />
      ) : (
        <div className="ranking-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 2, right: 20, bottom: 2, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#edf1ee" />
              <XAxis type="number" allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
              <YAxis
                type="category"
                dataKey="name"
                width={118}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => String(value).length > 18 ? String(value).slice(0, 18) + "…" : String(value)}
              />
              <Tooltip />
              <Bar dataKey="value" name="Quantidade" fill={color} radius={[0, 7, 7, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </article>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [units, setUnits] = useState<CompanyUnit[]>([]);
  const [unitId, setUnitId] = useState("");
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<{ units: CompanyUnit[] }>("units").then((result) => setUnits(result.units)).catch(() => setUnits([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const unitQuery = unitId ? "&unit_id=" + encodeURIComponent(unitId) : "";
    api<Dashboard>(`dashboard?days=${days}${unitQuery}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [days, unitId]);

  const trend = useMemo(
    () => (data?.trend ?? []).map((item) => ({ ...item, label: formatDate(item.date) })),
    [data]
  );

  const kpis = [
    {
      label: "Veículos ativos",
      value: data?.vehicles ?? "—",
      helper: "Frota cadastrada e ativa",
      icon: Truck,
      tone: "green"
    },
    {
      label: "Inspeções",
      value: data?.inspections_period ?? "—",
      helper: `Últimos ${days} dias`,
      icon: ClipboardCheck,
      tone: "blue"
    },
    {
      label: "Conformidade",
      value: data ? `${data.compliance_rate}%` : "—",
      helper: "Itens respondidos como conformes",
      icon: Gauge,
      tone: "green"
    },
    {
      label: "NCs abertas",
      value: data?.open_ncs ?? "—",
      helper: "Pendentes de tratamento",
      icon: AlertTriangle,
      tone: "amber"
    },
    {
      label: "Em manutenção",
      value: data?.maintenance_open ?? "—",
      helper: "Veículos/equipamentos indisponíveis",
      icon: Wrench,
      tone: "red"
    }
  ];

  return (
    <section className="dashboard-page">
      <div className="dashboard-hero">
        <div>
          <p className="eyebrow light">MM FLEET</p>
          <p className="dashboard-product-subtitle">Gestão e Inspeção de Frota</p>
          <h1>Olá, {user?.name?.split(" ")[0] || "Equipe"}!</h1>
          <p>Visão gerencial da frota, inspeções, não conformidades e desempenho operacional da MM Locações.</p>
        </div>
        <div className="hero-actions">
          <label className="period-control">
            <span>Período</span>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>7 dias</option>
              <option value={30}>30 dias</option>
              <option value={90}>90 dias</option>
              <option value={365}>12 meses</option>
            </select>
          </label>
          <label className="period-control">
            <span>Unidade</span>
            <select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
              <option value="">Todas as unidades</option>
              {units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
            </select>
          </label>
          <Link to="/inspecoes" className="hero-primary-action">
            <Plus size={19} />
            Nova inspeção
          </Link>
        </div>
      </div>

      <div className="metric-grid metric-grid-five">
        {kpis.map(({ label, value, helper, icon: Icon, tone }) => (
          <article className={`metric-card metric-${tone}`} key={label}>
            <div className="metric-icon"><Icon size={22} /></div>
            <div className="metric-copy">
              <span>{label}</span>
              <strong>{loading ? "…" : value}</strong>
              <small>{helper}</small>
            </div>
          </article>
        ))}
      </div>

      <div className="dashboard-grid">
        <article className="dashboard-card">
          <div className="card-heading">
            <div>
              <p className="section-kicker">FROTA</p>
              <h2>Status operacional</h2>
            </div>
            <ShieldCheck size={22} />
          </div>
          {(data?.vehicles_by_status.length ?? 0) === 0 ? (
            <ChartEmpty text="Cadastre veículos para visualizar a distribuição." />
          ) : (
            <div className="donut-layout">
              <div className="donut-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data?.vehicles_by_status ?? []} dataKey="value" nameKey="name" innerRadius={58} outerRadius={82} paddingAngle={3}>
                      {(data?.vehicles_by_status ?? []).map((item) => (
                        <Cell key={item.name} fill={statusColors[item.name] || "#829087"} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, statusLabels[String(name)] || String(name)]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-center">
                  <strong>{data?.vehicles ?? 0}</strong>
                  <span>ativos</span>
                </div>
              </div>
              <DonutLegend items={data?.vehicles_by_status ?? []} labels={statusLabels} colors={statusColors} />
            </div>
          )}
        </article>

        <article className="dashboard-card">
          <div className="card-heading">
            <div>
              <p className="section-kicker">INSPEÇÕES</p>
              <h2>Resultado no período</h2>
            </div>
            <CheckCircle2 size={22} />
          </div>
          {(data?.inspection_results.length ?? 0) === 0 ? (
            <ChartEmpty text="As inspeções aparecerão aqui assim que forem realizadas." />
          ) : (
            <div className="donut-layout">
              <div className="donut-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data?.inspection_results ?? []} dataKey="value" nameKey="name" innerRadius={58} outerRadius={82} paddingAngle={3}>
                      {(data?.inspection_results ?? []).map((item) => (
                        <Cell key={item.name} fill={resultColors[item.name] || "#829087"} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, resultLabels[String(name)] || String(name)]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-center">
                  <strong>{data?.inspections_period ?? 0}</strong>
                  <span>inspeções</span>
                </div>
              </div>
              <DonutLegend items={data?.inspection_results ?? []} labels={resultLabels} colors={resultColors} />
            </div>
          )}
        </article>

        <article className="dashboard-card dashboard-card-wide">
          <div className="card-heading">
            <div>
              <p className="section-kicker">EVOLUÇÃO</p>
              <h2>Inspeções ao longo do período</h2>
            </div>
            <span className="card-badge">{days} dias</span>
          </div>
          {trend.length === 0 ? (
            <ChartEmpty />
          ) : (
            <div className="trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="inspectionGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#17834b" stopOpacity={0.32} />
                      <stop offset="95%" stopColor="#17834b" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e6ece8" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="value" name="Inspeções" stroke="#17834b" strokeWidth={3} fill="url(#inspectionGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </article>

        <HorizontalBars
          kicker="QUALIDADE"
          title="Veículos com mais NCs"
          data={data?.vehicles_with_most_ncs ?? []}
          color="#cc5252"
          empty="Nenhuma NC registrada no período."
        />

        <HorizontalBars
          kicker="DEFEITOS RECORRENTES"
          title="Principais itens com NC"
          data={data?.top_defects ?? []}
          color="#d29a2e"
          empty="Nenhum defeito recorrente no período."
        />

        <HorizontalBars
          kicker="PRODUTIVIDADE"
          title="Inspeções por inspetor"
          data={data?.inspections_by_inspector ?? []}
          color="#3478c9"
        />

        <HorizontalBars
          kicker="FROTA"
          title="Inspeções por tipo de veículo"
          data={data?.inspections_by_vehicle_type ?? []}
          color="#137a43"
        />

        <article className="dashboard-card">
          <div className="card-heading">
            <div>
              <p className="section-kicker">NÃO CONFORMIDADES</p>
              <h2>NCs abertas por gravidade</h2>
            </div>
            <AlertTriangle size={22} />
          </div>
          {(data?.ncs_by_severity.length ?? 0) === 0 ? (
            <div className="healthy-state">
              <CheckCircle2 size={34} />
              <strong>Nenhuma NC aberta</strong>
              <span>A situação atual está regular.</span>
            </div>
          ) : (
            <div className="severity-list">
              {(data?.ncs_by_severity ?? []).map((item) => {
                const total = Math.max(1, data?.open_ncs ?? 1);
                return (
                  <div className="severity-row" key={item.name}>
                    <div className="severity-top">
                      <span><i style={{ background: severityColors[item.name] || "#829087" }} />{severityLabels[item.name] || item.name}</span>
                      <strong>{item.value}</strong>
                    </div>
                    <div className="severity-track">
                      <span style={{ width: `${Math.max(8, (item.value / total) * 100)}%`, background: severityColors[item.name] || "#829087" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </article>

        <article className="dashboard-card">
          <div className="card-heading">
            <div>
              <p className="section-kicker">DESEMPENHO</p>
              <h2>Índice de conformidade</h2>
            </div>
            <UserRoundCheck size={22} />
          </div>
          <div className="compliance-box">
            <div className="compliance-ring" style={{ "--progress": `${data?.compliance_rate ?? 0}%` } as React.CSSProperties}>
              <strong>{data?.compliance_rate ?? 0}%</strong>
            </div>
            <div>
              <strong>Itens conformes</strong>
              <p>Percentual de respostas marcadas como “Conforme” dentro do período selecionado.</p>
            </div>
          </div>
        </article>

        <article className="dashboard-card dashboard-card-wide">
          <div className="card-heading">
            <div>
              <p className="section-kicker">ATIVIDADE RECENTE</p>
              <h2>Últimas inspeções</h2>
            </div>
            <Link to="/inspecoes" className="text-link">Ver histórico <ArrowRight size={15} /></Link>
          </div>
          {(data?.recent_inspections.length ?? 0) === 0 ? (
            <ChartEmpty text="Nenhuma inspeção registrada até o momento." />
          ) : (
            <div className="recent-list">
              {(data?.recent_inspections ?? []).map((item) => (
                <div className="recent-item" key={item.id}>
                  <div className="recent-icon"><ClipboardCheck size={18} /></div>
                  <div className="recent-main">
                    <strong>{item.identifier}</strong>
                    <small>{item.plate || "Sem placa"} · {item.unit_name || "Unidade não definida"} · {item.inspector}</small>
                  </div>
                  <span className={`result ${item.result}`}>{resultLabels[item.result]}</span>
                  <time>{formatDateTime(item.created_at)}</time>
                </div>
              ))}
            </div>
          )}
        </article>
      </div>
    </section>
  );
}
